// frontend/packages/business-console/src/components/build-interview/AssemblyPreview.jsx
//
// "Assembling your homepage" preview shown on the Building screen (Website
// Builder Redesign, Phase 1): a mini browser frame whose nav + hero render
// solid while the service cards stream in as skeletons and later sections show
// as "up next". A generic, representative layout (the real live preview is a
// later phase); colors via useTokens.

import React from 'react';
import { Box, Typography } from '@mui/material';
import useTokens from '../primitives/useTokens';

function SkeletonCard({ tokens, delay = 0 }) {
  const t = tokens;
  return (
    <Box
      data-bi-anim
      sx={{
        flex: 1,
        height: 64,
        borderRadius: '8px',
        border: `1px solid ${t.hairline}`,
        background: t.subtle,
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(90deg, transparent, ${t.grayBg}, transparent)`,
          animation: 'bi-shimmer 1.3s linear infinite',
          animationDelay: `${delay}s`,
        },
      }}
    />
  );
}

export default function AssemblyPreview() {
  const t = useTokens();
  return (
    <Box
      sx={{
        background: t.card,
        border: `1px solid ${t.cardBorder}`,
        borderRadius: '16px',
        p: 1.5,
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '.06em',
          textTransform: 'uppercase',
          color: t.faint,
          mb: 1,
        }}
      >
        Assembling your homepage
      </Typography>
      <Box
        sx={{
          borderRadius: '12px',
          overflow: 'hidden',
          border: `1px solid ${t.hairline}`,
        }}
      >
        {/* nav (solid) */}
        <Box
          sx={{
            background: t.gradientHero,
            px: 1.5,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ width: 60, height: 10, borderRadius: 4, background: 'rgba(255,255,255,.85)' }} />
          <Box sx={{ width: 44, height: 14, borderRadius: 6, background: 'rgba(255,255,255,.35)' }} />
        </Box>
        {/* hero (solid) */}
        <Box sx={{ background: t.gradientHero, px: 1.5, pb: 2 }}>
          <Box sx={{ width: '70%', height: 12, borderRadius: 4, background: 'rgba(255,255,255,.85)', mb: 0.75 }} />
          <Box sx={{ width: '50%', height: 8, borderRadius: 4, background: 'rgba(255,255,255,.5)' }} />
        </Box>
        {/* service cards (streaming skeletons) */}
        <Box sx={{ p: 1.5, background: t.card }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <SkeletonCard tokens={t} delay={0} />
            <SkeletonCard tokens={t} delay={0.2} />
            <SkeletonCard tokens={t} delay={0.4} />
          </Box>
          <Box
            sx={{
              border: `1px dashed ${t.grayBorder}`,
              borderRadius: '8px',
              p: 1,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontSize: 10.5, color: t.faint }}>
              Service area and contact, up next
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
